import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import Skeleton from 'react-loading-skeleton';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import IconWrapper from '@components/ui/Icons';

import style from './PromotionalTicker.module.scss';

enum AnimationState {
  static = 'animationStateStatic',
  in = 'animationStateIn',
  out = 'animationStateOut',
}

type PromotionalTickerProps = {
  messages: PromoTicker[];
  refreshTime?: number;
};

const PromotionalTicker = ({
  messages = [],
  refreshTime = 6000,
}: PromotionalTickerProps) => {
  const fadeTime = 500;
  const [counter, setCounter] = useState(0);
  const [animationState, setAnimationState] = useState(AnimationState.static);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let tid: NodeJS.Timeout;

    function handleStateOut() {
      setAnimationState(AnimationState.out);
    }

    function handleStateStatic() {
      setAnimationState(AnimationState.static);
    }

    function handleStateIn() {
      setAnimationState(AnimationState.in);
      setCounter((counter + 1) % messages.length);
    }

    function handleNextStep(nextState: AnimationState) {
      if (nextState === AnimationState.out) handleStateOut();
      else if (nextState === AnimationState.in) handleStateIn();
      else if (nextState === AnimationState.static) handleStateStatic();
    }

    if (messages?.length > 1) {
      // Duration of next step based on completed step | Static -> Out -> In -> Static
      let nextState = AnimationState.static;
      if (animationState === AnimationState.static)
        nextState = AnimationState.out;
      else if (animationState === AnimationState.out)
        nextState = AnimationState.in;

      // CurrentStep: Out|fadeTime, In|fadeTime, Static|refreshTime
      let duration = refreshTime;
      if (
        animationState === AnimationState.out ||
        animationState === AnimationState.in
      )
        duration = fadeTime;

      // Set Timeout
      tid = setTimeout(() => handleNextStep(nextState), duration);
    }

    return () => clearTimeout(tid);
  }, [counter, animationState, messages?.length, refreshTime]);

  if (!messages?.length) return <Skeleton width={100} />;

  return (
    <div className={style.promotionalTickerContainer} ref={containerRef}>
      {messages.map((message, i) => {
        const spacerCount = messages.length - 1;
        const displayWidth = containerRef?.current?.offsetWidth || 0;
        const itemHeight = displayWidth > 0 ? 'auto' : '0px';
        const isCurrent = counter === i;
        const stateStyle = isCurrent
          ? style[animationState]
          : style.promotionalTickerHidden;
        const linkStyle = !isCurrent
          ? { minWidth: `${displayWidth - spacerCount}px` }
          : {};

        return (
          <div
            key={`${i}-${message?.promo_text}`}
            style={{ height: itemHeight }}
            className={cn(style.promotionalTickerItem, stateStyle)}
          >
            <a
              style={linkStyle}
              className={style.promotionalTickerLink}
              href={message?.promo_link.url}
            >
              {!message?.icon && (
                <span className={style.promotionalTickerText}>
                  {message?.promo_text} &gt;
                </span>
              )}

              {!!message?.icon && (
                <>
                  <span className={style.promotionalTickerText}>
                    {message?.promo_text}
                  </span>
                  <IconWrapper
                    classNames={style.promotionalTickerIcon}
                    icon={message.icon.id}
                    width={message.icon.width}
                    height={message.icon.height}
                  />
                </>
              )}
            </a>
          </div>
        );
      })}
    </div>
  );
};

export default PromotionalTicker;
